/**
 * Bugs & Features board tables (the feedback Postgres store).
 *
 * The board started on a JSON file (server/services/feedback-board/store.ts).
 * These tables are the Postgres home it moves to: one row per report, vote and
 * status-history entry. They are created by the hand-run manual migration
 * drizzle/manual/0105_feedback-approved-for-work.sql (the drizzle journal is out
 * of step with drizzle/, MIS-86, so the board's tables are not journaled); the
 * SQL there must stay in step with these definitions.
 *
 * Ids and org ids are plain text, not uuid/foreign keys: the rebuild script for
 * the lost reports stores a non-UUID "[lost …]" sentinel as the org id so those
 * rows belong to no real firm, and reporter ids may be lost too. The whole
 * FeedbackItemRecord is kept in `record` (jsonb); the scalar columns mirror the
 * fields the board and the migration filter or flag on (status, published,
 * approved_for_work), so neither a query nor 0105 has to open the jsonb.
 *
 * approved_for_work is internal work-tracking only. It never makes a report
 * publicly visible and never unmasks a title — publishing to the public roadmap
 * is a separate admin action and lives in `record.publication` / the `published`
 * mirror column.
 */
import {
  pgTable,
  boolean,
  index,
  jsonb,
  primaryKey,
  text,
  timestamp,
} from 'drizzle-orm/pg-core';

export const feedbackItems = pgTable(
  'feedback_items',
  {
    id: text('id').primaryKey(),
    orgId: text('org_id').notNull(),
    type: text('type').notNull(),
    status: text('status').notNull(),
    /** Internal work-tracking flag; independent of publication. New reports default true. */
    approvedForWork: boolean('approved_for_work').notNull().default(true),
    /** Mirror of record.publication !== null, so the roadmap/admin filters stay out of the jsonb. */
    published: boolean('published').notNull().default(false),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull(),
    /** The full FeedbackItemRecord (store.ts). The source of truth; the scalar columns mirror it. */
    record: jsonb('record').notNull(),
  },
  (table) => [
    index('idx_feedback_items_org_id').on(table.orgId),
    index('idx_feedback_items_status').on(table.status),
    index('idx_feedback_items_published').on(table.published),
  ],
);

export const feedbackVotes = pgTable(
  'feedback_votes',
  {
    itemId: text('item_id').notNull(),
    userId: text('user_id').notNull(),
    /** The voter's firm, or null for a ClaimHive admin with no firm. */
    orgId: text('org_id'),
    value: text('value').notNull(),
    votedAt: timestamp('voted_at', { withTimezone: true }).notNull(),
  },
  (table) => [
    primaryKey({ columns: [table.itemId, table.userId] }),
    index('idx_feedback_votes_item_id').on(table.itemId),
  ],
);

export const feedbackStatusEvents = pgTable(
  'feedback_status_events',
  {
    id: text('id').primaryKey(),
    itemId: text('item_id').notNull(),
    orgId: text('org_id').notNull(),
    fromStatus: text('from_status').notNull(),
    toStatus: text('to_status').notNull(),
    changedByUserId: text('changed_by_user_id').notNull(),
    changedByName: text('changed_by_name'),
    changedAt: timestamp('changed_at', { withTimezone: true }).notNull(),
    /** "auto_triaged" | "hold_approved" | "hold_rejected"; null for a person's plain status change. */
    kind: text('kind'),
    note: text('note'),
  },
  (table) => [index('idx_feedback_status_events_item_id').on(table.itemId)],
);
